import React from 'react';
import { useRef, useMemo } from 'react';
import { Box, Typography } from '@mui/material';

import { PageHeader, SectionContent } from '../../components';
import { IMPORTANT_DISCLOSURES } from '../../data';
import THEME from '../../constants/theme';
import {
  SECTION_SPACING,
  SUBSECTION_SPACING,
  ELEMENT_SPACING
} from '../../constants';
import SEO from '../../components/SEO';
import { seoData } from '../../data/seoData';

const ImportantDisclosuresPage = ({ setRefs }) => {
  const importantDisclosuresRef = useRef(null);
  const noGuaranteeRef = useRef(null);
  const liabilityRef = useRef(null);
  const intellectualPropertyRef = useRef(null);
  const recordingAndDataRef = useRef(null);

  const refs = useMemo(
    () => ({
      importantDisclosures: importantDisclosuresRef,
      noGuarantee: noGuaranteeRef,
      liability: liabilityRef,
      intellectualProperty: intellectualPropertyRef,
      recordingAndData: recordingAndDataRef
    }),
    []
  );

  // Update global refs if setRefs function is provided
  React.useEffect(() => {
    if (setRefs && typeof setRefs === 'function') {
      setRefs(refs);
    }
  }, [refs, setRefs]);

  return (
    <>
      <SEO
        title={seoData.importantDisclosures.title}
        content={seoData.importantDisclosures.content}
        keywords={seoData.importantDisclosures.keywords}
        canonical={seoData.importantDisclosures.canonical}
        ogImage={seoData.importantDisclosures.ogImage}
      />
      {/* Page Header */}}
      <PageHeader
        id='hero'
        title={IMPORTANT_DISCLOSURES.TITLE}
        align='left'
        sx={{ mb: SECTION_SPACING }}
      />
      {/* Introduction */}
      <SectionContent
        id='importantDisclosures'
        ref={refs.importantDisclosures}
        initialSpacing={false}
        text={[
          {
            text: IMPORTANT_DISCLOSURES.LAST_UPDATED,
            marginBottom: ELEMENT_SPACING,
            fontStyle: 'italic',
            fontSize: '1.1rem',
            color: THEME.COLORS.NEUTRAL_GRAY_DARK
          },
          {
            text: IMPORTANT_DISCLOSURES.INTRO,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />
      {/* No Guarantee of Performance section */}
      <SectionContent
        id='noGuarantee'
        ref={refs.noGuarantee}
        title={IMPORTANT_DISCLOSURES.SECTIONS.NO_GUARANTEE.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={IMPORTANT_DISCLOSURES.SECTIONS.NO_GUARANTEE.PARAGRAPHS.map(
          paragraph => ({
            text: paragraph,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          })
        )}
      />
      {/* Liability Disclaimer section */}
      <SectionContent
        id='liability'
        ref={refs.liability}
        title={IMPORTANT_DISCLOSURES.SECTIONS.LIABILITY.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={IMPORTANT_DISCLOSURES.SECTIONS.LIABILITY.PARAGRAPHS.map(
          paragraph => ({
            text: paragraph,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          })
        )}
      />
      {/* Intellectual Property section */}
      <SectionContent
        id='intellectualProperty'
        ref={refs.intellectualProperty}
        title={IMPORTANT_DISCLOSURES.SECTIONS.INTELLECTUAL_PROPERTY.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={IMPORTANT_DISCLOSURES.SECTIONS.INTELLECTUAL_PROPERTY.PARAGRAPHS.map(
          paragraph => ({
            text: paragraph,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          })
        )}
      />
      {/* Recording of Calls section */}
      <SectionContent
        id='recordingAndData'
        ref={refs.recordingAndData}
        title={IMPORTANT_DISCLOSURES.SECTIONS.RECORDING_AND_DATA.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={IMPORTANT_DISCLOSURES.SECTIONS.RECORDING_AND_DATA.PARAGRAPHS.map(
          paragraph => ({
            text: paragraph,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          })
        )}
      />
      {/* Footer note with separator line */}
      <Box
        sx={{
          borderTop: `1px solid ${THEME.COLORS.NEUTRAL_GRAY}`,
          paddingTop: ELEMENT_SPACING,
          paddingBottom: ELEMENT_SPACING,
          mt: 4,
          mb: 2
        }}
      >
        <Typography
          variant='body2'
          sx={{
            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
            fontSize: '1rem',
            fontStyle: 'italic',
            color: THEME.COLORS.NEUTRAL_GRAY,
            lineHeight: 1.6,
            display: 'block'
          }}
        >
          {IMPORTANT_DISCLOSURES.FOOTER_NOTE}
        </Typography>
      </Box>
    </>
  );
};

export default ImportantDisclosuresPage;
